import type React from "react"

import { clsx } from "clsx"
import Image from "next/image"
import { twMerge } from "tailwind-merge"

import { PlaceholderImage } from "@/components/flow/icons/PlaceholderImage"
import type { StoreProductImage } from "@/platform/api/store-contract"

export type ThumbnailSize = "small" | "medium" | "large" | "full" | "square" | "square-small"

interface ThumbnailProps {
  thumbnail?: string | null
  images?: StoreProductImage[] | null
  size?: ThumbnailSize
  isFeatured?: boolean
  className?: string
}

export const Thumbnail: React.FC<ThumbnailProps> = ({ thumbnail, images, size = "small", isFeatured, className }) => {
  const initialImage = thumbnail || images?.[0]?.url

  return (
    <div
      className={twMerge(
        clsx("rounded-large relative w-full overflow-hidden p-4 transition-shadow duration-150 ease-in-out", className, {
          "aspect-[11/14]": isFeatured,
          "aspect-[9/16]": !isFeatured && size !== "square",
          "aspect-[1/1]": size.includes("square"),
          "w-[100px]": size === "square-small",
          "w-[180px]": size === "small",
          "w-[290px]": size === "medium",
          "w-[440px]": size === "large",
          "w-full": size === "full",
        })
      )}
    >
      <ImageOrPlaceholder image={initialImage} size={size} />
    </div>
  )
}

const ImageOrPlaceholder = ({ image, size }: Pick<ThumbnailProps, "size"> & { image?: string }) => {
  return image ? (
    <Image
      src={image}
      alt="Thumbnail"
      className="absolute inset-0 bg-white object-cover object-center transition-transform duration-300 ease-in-out group-hover:scale-105"
      draggable={false}
      quality={50}
      sizes="(max-width: 576px) 280px, (max-width: 768px) 360px, (max-width: 992px) 480px, 800px"
      style={{ objectFit: "contain" }}
      fill
    />
  ) : (
    <div className="absolute inset-0 flex h-full w-full items-center justify-center">
      <PlaceholderImage size={size === "small" ? 16 : 24} />
    </div>
  )
}
